
import { onMounted, ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store/index'
import { removeLocaleFromPath } from '@/utils/commonUtils'
import { supportedLocales } from '@/core/i18n'
import { useLang } from '@/hook/useLang'
import { commonApi } from '@/core/api/commonApi'
import { storeToRefs } from 'pinia'
import { useDialog } from '@/hook/useDialog'

export default {
  name: 'HeaderComponent',
  components: {
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const lang = useLang()
    const path = computed(() => removeLocaleFromPath(route.path))
    const { alertDialog } = useDialog()

    const languages = ref(supportedLocales)
    const currentLang = ref('en')

    const store = useStore()
    const { saveTime } = storeToRefs(store)

    const isExpired = ref(false)

    const pageTitle = computed(() => {
      const title = route.meta.pageTitle
      if (typeof title === 'string') {
        return t(title)
      }
      return null
    })

    const isLanguagesPopupOpen = ref(false)

    /**
     * 유효시간 체크 제외 url
     */
    const expiryCheckExcludedPath = [
      /^\/test/,
      /^\/$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)\/(city|terminal|history)\/consent\/privacy$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)\/check-wifi$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)\/guide$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)\/tips$/,
      /^\/(kr|en|zh_cn|zh_hk|jp|th)\/wifi$/
    ]

    // 뒤로 이동
    const moveToBack = () => {
      router.back()
    }

    // 홈으로 이동
    const moveToHome = async () => {
      router.push(`/${lang.value.code}`)
    }

    // 가이드 페이지로 이동
    const moveToGuide = () => {
      router.push(`/${lang.value.code}/guide`)
    }

    // 프로세스 진행 만료 시간 체크
    const checkSaveTime = () => {
      if (!saveTime.value) {
        isExpired.value = false
        return
      }
      // 개인정보 수집 동의 후 6시간 유지
      const expiryTime = new Date(Number(saveTime.value))
      expiryTime.setHours(expiryTime.getHours() + 6)
      const now = new Date()

      isExpired.value = expiryTime < now
    }

    // 언어 선택 팝업 배경 클릭 이벤트
    const onClickLanguagesBackground = (e: Event) => {
      if (e.target === e.currentTarget) {
        isLanguagesPopupOpen.value = false
      }
    }

    // 언어 선택
    const selectLanguage = (lang: string) => {
      isLanguagesPopupOpen.value = false
      router.replace(`/${lang}`)
    }

    onMounted(() => {
      checkSaveTime()
      currentLang.value = lang.value.code
    })

    watch(route, () => {
      currentLang.value = lang.value.code

      if (route.params.service === 'terminal') {
        commonApi.checkBoothTml()
          .then(connected => {
            if (!connected) {
              router.push(`/${lang.value.code}/check-wifi`)
            }
          })
      }

      // 페이지 이동 시 마다 saveTime Check
      if (!expiryCheckExcludedPath.some(reg => location.pathname.match(reg))) {
        checkSaveTime()
      }

      // 스크롤 초기화 (index.ts 의 beforeEach에서 initial로 초기화)
      document.documentElement.style.scrollBehavior = 'smooth'
      window.scrollTo({ top: 0 })
    })

    watch(isLanguagesPopupOpen, (open) => {
      if (open) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'unset'
      }
    })

    watch(isExpired, (value) => {
      if (value) {
        moveToHome()
        alertDialog({
          title: t('expired_alert'),
          description: ''
        })
      }
    })

    return {
      path,
      languages,
      currentLang,
      pageTitle,
      isLanguagesPopupOpen,
      moveToBack,
      moveToHome,
      moveToGuide,
      onClickLanguagesBackground,
      selectLanguage
    }
  }
}
